import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowUpRight, ArrowRight, Check } from "lucide-react";
import { SERVICE_DETAILS } from "@/data/servicesDetail";
import TechBg from "@/components/TechBg";

export default function ServiceDetail() {
  const { slug } = useParams();
  const data = SERVICE_DETAILS[slug];
  if (!data) return <Navigate to="/services" replace />;

  return (
    <div data-testid={`service-detail-page-${slug}`}>
      {/* HERO */}
      <section className="relative overflow-hidden border-b border-[#1f1f22]">
        <TechBg />
        <div className="container-x relative pt-20 md:pt-28 pb-20 z-10">
          <div className="grid lg:grid-cols-[1.2fr_1fr] gap-12 items-center">
            <div>
              <div className="label-mono mb-6">// {data.title}</div>
              <h1 className="font-display text-[clamp(36px,5.5vw,72px)] font-black uppercase tracking-tight leading-[0.96] text-white">
                {data.tagline}
              </h1>
              <p className="mt-8 text-lg text-zinc-400 max-w-xl leading-relaxed">{data.hero}</p>
              <div className="mt-10 flex flex-wrap gap-4">
                <Link to="/contact" data-testid={`${slug}-cta-primary`} className="btn-primary">
                  Get Free Quote <ArrowUpRight size={16} />
                </Link>
                <Link to="/services" data-testid={`${slug}-cta-secondary`} className="btn-ghost">
                  All Services <ArrowRight size={16} />
                </Link>
              </div>
            </div>
            <div className="relative">
              <div className="border border-[#27272A] bg-[#0E0E10] p-1">
                <div className="border border-[#1f1f22] overflow-hidden">
                  <img src={data.image} alt={data.title} className="w-full h-72 lg:h-96 object-cover grayscale-[40%]" loading="lazy" />
                </div>
              </div>
              <div className="absolute -bottom-6 -left-6 border border-[#DC1F33]/40 bg-[#09090B]/95 backdrop-blur px-5 py-4 hidden md:block">
                <div className="label-mono text-[#DC1F33] mb-1">Live</div>
                <div className="font-display text-white text-lg">Production-ready in weeks</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* METRICS */}
      <section className="border-b border-[#1f1f22]">
        <div className="container-x">
          <div className="grid grid-cols-1 md:grid-cols-3 border-l border-t border-[#1f1f22]">
            {data.metrics.map((m) => (
              <div key={m.l} className="grid-cell">
                <div className="font-display text-5xl md:text-6xl font-black text-[#DC1F33] tracking-tight">{m.v}</div>
                <div className="label-mono mt-3">{m.l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* OFFERINGS */}
      <section className="section border-b border-[#1f1f22]">
        <div className="container-x">
          <div className="label-mono mb-4">// What we deliver</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] max-w-3xl mb-14">
            End-to-end {data.title.toLowerCase()} <span className="text-[#DC1F33]">capabilities.</span>
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 border-l border-t border-[#1f1f22]">
            {data.offerings.map((o, i) => (
              <div key={o.t} data-testid={`offering-${i}`} className="grid-cell">
                <div className="font-display text-xs label-mono text-[#DC1F33] mb-3">0{i + 1}</div>
                <div className="font-display text-xl font-bold text-white mb-3 tracking-tight">{o.t}</div>
                <p className="text-sm text-zinc-400 leading-relaxed">{o.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* STACK */}
      <section className="section border-b border-[#1f1f22] bg-[#0B0B0D]">
        <div className="container-x">
          <div className="label-mono mb-4">// Tools & technologies</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] mb-12">Built with the right stack.</h2>
          <div className="flex flex-wrap gap-3">
            {data.stack.map((s) => (
              <div key={s} className="border border-[#27272A] px-5 py-3 font-display tracking-tight text-zinc-200 hover:border-[#DC1F33] hover:text-[#DC1F33] transition-colors">
                {s}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section className="section border-b border-[#1f1f22]">
        <div className="container-x">
          <div className="label-mono mb-4">// How we work</div>
          <h2 className="font-display text-4xl md:text-5xl font-black uppercase tracking-tight text-white leading-[0.95] mb-14 max-w-3xl">
            From <span className="text-[#DC1F33]">brief to launch,</span> in 4 phases.
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 border-l border-t border-[#1f1f22]">
            {data.process.map((p, i) => (
              <div key={p.t} className="grid-cell">
                <div className="font-display text-5xl font-black text-[#DC1F33] mb-6">0{i + 1}</div>
                <div className="font-display text-xl font-bold text-white mb-3 tracking-tight">{p.t}</div>
                <p className="text-sm text-zinc-400 leading-relaxed">{p.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section">
        <div className="container-x">
          <div className="border border-[#27272A] p-12 md:p-16 bg-[#0E0E10] flex flex-col md:flex-row md:items-center justify-between gap-8">
            <div>
              <div className="label-mono mb-3">// Ready to start</div>
              <h3 className="font-display text-3xl md:text-4xl font-black uppercase tracking-tight text-white leading-tight">Let's build your {data.title.toLowerCase()}.</h3>
              <p className="text-zinc-400 mt-3 max-w-xl">Free 30-min discovery call. You'll walk away with a clear roadmap and honest timeline.</p>
            </div>
            <Link to="/contact" data-testid={`${slug}-final-cta`} className="btn-primary whitespace-nowrap">
              Book Consultation <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
